import type { ReactNode } from "react";

/** The Activity card on Home (the home-and-customers round, 2026-11-03; the founder's variation A, "Are we keeping up?"): the open tickets
 *  now in large, a sentence about the period's arrivals against its resolutions, two thin bars per day (`FlowBars`) with their legend and the
 *  period's dates, and a grid of small figures, value over label, in equal cells (three to a row, two on a phone) so nothing wraps mid-fact.
 *  Props in: the screen writes every word and number. The bars are two greys of the data ink; never the AI hue. */

export type FlowBar = { key: string; /** the hover text, written by the screen: "Oct 28 · 44 arrived · 19 resolved" */ title: string; arrived: number; resolved: number };

const W = 560, H = 96, PAIR_GAP = 2, BAR_MAX = 12, RADIUS = 3;

/** two bars per day (or week) as one inline picture: arrived in the ink, resolved in the lighter ink, rounded ends, a 2-unit gap inside a
 *  pair, on a hairline baseline; heights relative to the period's largest number; a zero day keeps its two bars at height 0. Each pair is a
 *  group with its own title (the hover text); the picture's spoken name sums the period. */
export function FlowBars({ bars, label }: { bars: FlowBar[]; /** the picture's spoken name; by default the period's two sums */ label?: string }) {
  const top = Math.max(1, ...bars.map((b) => Math.max(b.arrived, b.resolved)));
  const slot = W / Math.max(1, bars.length);
  const barW = Math.max(2, Math.min(BAR_MAX, (slot - PAIR_GAP) * 0.36));
  const height = (v: number) => Math.round((v / top) * (H - 6) * 10) / 10;
  const sum = (k: "arrived" | "resolved") => bars.reduce((n, b) => n + b[k], 0);
  const spoken = label ?? `${sum("arrived")} arrived, ${sum("resolved")} resolved`;
  return (
    <svg className="ob-flow-bars" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMax meet" role="img" aria-label={spoken}>
      {bars.map((b, i) => {
        const x = i * slot + (slot - (2 * barW + PAIR_GAP)) / 2;
        const ha = height(b.arrived), hr = height(b.resolved);
        return (
          <g key={b.key} className="ob-flow-bars__day" pointerEvents="bounding-box">
            <title>{b.title}</title>
            <rect className="ob-flow-bars__bar" x={x} y={H - 1 - ha} width={barW} height={ha} rx={RADIUS} fill="var(--ob-ink)" />
            <rect className="ob-flow-bars__bar" x={x + barW + PAIR_GAP} y={H - 1 - hr} width={barW} height={hr} rx={RADIUS} fill="var(--ob-ink-3)" />
          </g>
        );
      })}
      <line className="ob-flow-bars__base" x1={0} x2={W} y1={H - 0.5} y2={H - 0.5} stroke="var(--ob-border)" strokeWidth={1} />
    </svg>
  );
}

export type ActivityFact = { value: string; label: string };

export function ActivityCard({ open, change, bars, dates, facts, action }: {
  /** the team's open tickets now (the All open view's number) */ open: number;
  /** the sentence under the number: what the bars show in words */ change: string;
  bars: FlowBar[]; /** the period's dates, right of the legend: "Oct 21 – Nov 3" */ dates: string;
  /** the small figures, in order */ facts: ActivityFact[];
  /** optional: a link around or beside the open number (the screen's) */ action?: ReactNode;
}) {
  return (
    <div className="ob-activity">
      <div className="ob-activity__hero"><span className="ob-activity__value">{open}</span><span className="ob-activity__unit">{open === 1 ? "open ticket" : "open tickets"}</span>{action}</div>
      <p className="ob-activity__change">{change}</p>
      <div className="ob-activity__chart"><FlowBars bars={bars} /></div>
      <div className="ob-activity__legend">
        <span className="ob-activity__key ob-activity__key--arrived">Arrived</span>
        <span className="ob-activity__key ob-activity__key--resolved">Resolved</span>
        <span className="ob-activity__dates">{dates}</span>
      </div>
      <div className="ob-activity__facts">
        {facts.map((f) => <div key={f.label} className="ob-activity__fact"><span className="ob-activity__fact-value">{f.value}</span><span className="ob-activity__fact-label">{f.label}</span></div>)}
      </div>
    </div>
  );
}
